// Responsive images for uploads. Cards and pages ask the backend for a resized
// WebP (/api/img?src=&w=) instead of the full original, which is often
// 2–17 MB and 5000–8000px wide. The browser picks the width it needs from
// srcSet + sizes, so phones get small files and retina screens stay sharp.
// Anything that isn't a site upload (data:, external URLs) is passed through.
const API = process.env.REACT_APP_API_URL || "";
const UPLOAD_RE = /^\/uploads\/(blog-images|profile-pics)\/[\w.-]+$/;

export const CARD_WIDTHS = [480, 640, 800, 960, 1200];
export const HERO_WIDTHS = [640, 960, 1200, 1600, 2400];
export const CONTENT_WIDTHS = [480, 800, 1200, 1600];

// "uploads\\x.png", "uploads/x.png" or "/uploads/x.png" → "/uploads/x.png"
export const normalizeUploadPath = (p) => {
  if (!p) return "";
  let s = String(p).trim().replace(/\\/g, "/");
  if (/^(https?:|data:|blob:)/i.test(s)) {
    // Absolute links to our own API host still count as uploads.
    if (API && s.startsWith(API + "/uploads/")) return s.slice(API.length);
    return s;
  }
  if (!s.startsWith("/")) s = "/" + s;
  return s;
};

export const isUpload = (p) => UPLOAD_RE.test(normalizeUploadPath(p));

// Full URL of the original file.
export const originalUrl = (p) => {
  const s = normalizeUploadPath(p);
  return s.startsWith("/") ? `${API}${s}` : s;
};

export const variantUrl = (p, w) => {
  const s = normalizeUploadPath(p);
  if (!UPLOAD_RE.test(s)) return originalUrl(s);
  return `${API}/api/img?src=${encodeURIComponent(s)}&w=${w}`;
};

// Props for an <img>: src (a sensible default width), srcSet and sizes.
export const responsiveImg = (p, { widths = CARD_WIDTHS, sizes = "100vw", fallback = 800 } = {}) => {
  const s = normalizeUploadPath(p);
  if (!UPLOAD_RE.test(s)) return { src: originalUrl(s) };
  return {
    src: variantUrl(s, fallback),
    srcSet: widths.map((w) => `${variantUrl(s, w)} ${w}w`).join(", "),
    sizes,
  };
};

// onError: if a variant fails, try the original once; then the given fallback
// (a placeholder image, or null to hide the image).
export const imgErrorFallback = (p, fallback) => (e) => {
  const img = e.currentTarget;
  if (!img.dataset.triedOriginal && isUpload(p)) {
    img.dataset.triedOriginal = "1";
    img.removeAttribute("srcset");
    img.src = originalUrl(p);
    return;
  }
  if (fallback) {
    // Flag, not `img.src !== fallback`: img.src is always absolute while the
    // fallback is usually relative, so that check never matched and a broken
    // fallback re-fired onError forever.
    if (img.dataset.triedFallback) return;
    img.dataset.triedFallback = "1";
    img.removeAttribute("srcset");
    img.src = fallback;
  } else {
    img.style.display = "none";
  }
};
